import { useEffect, useState } from "react";
import api, { eur } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Loader2, PackagePlus } from "lucide-react";

const r2 = (x) => Math.round(Number(x || 0) * 100) / 100;
const norm = (s) => String(s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();

export const consumableSettings = (s) => ({
  on: s.consumable_suggest !== false,
  pct: s.consumable_pct === undefined || s.consumable_pct === "" ? 5 : Number(s.consumable_pct) || 0,
  min: s.consumable_min === "" || s.consumable_min == null ? null : Number(s.consumable_min),
  max: s.consumable_max === "" || s.consumable_max == null ? null : Number(s.consumable_max),
  mode: s.consumable_mode === "stock" ? "stock" : "single",
});

export const hasConsumable = (items, consumableIds) => (items || []).some((it) => norm(it.description) === "spotrebny material" || (it.stock_item_id && consumableIds.has(it.stock_item_id)));

export const partsBase = (items, consumableIds) => r2((items || []).filter((it) => it.kind === "material" && !(it.stock_item_id && consumableIds.has(it.stock_item_id)) && norm(it.description) !== "spotrebny material")
  .reduce((s, it) => s + Number(it.qty || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100), 0));

export const suggestAmount = (base, cfg) => {
  let a = base * cfg.pct / 100;
  if (cfg.min != null && a < cfg.min) a = cfg.min;
  if (cfg.max != null && a > cfg.max) a = cfg.max;
  return r2(a);
};

function StockRows({ stock, sel, setSel }) {
  if (!stock.length) return <p className="rounded-lg border border-border bg-card/60 p-3 text-sm text-muted-foreground" data-testid="consumable-stock-empty">Na sklade nie je žiadny spotrebný materiál – pridá sa jedna položka „Spotrebný materiál“.</p>;
  return (
    <div className="max-h-56 space-y-1.5 overflow-y-auto" data-testid="consumable-stock-list">
      {stock.map((it) => (
        <div key={it.id} className="flex items-center gap-3 rounded-lg border border-border bg-card/60 p-2 text-sm">
          <Checkbox checked={sel[it.id] != null} onCheckedChange={(v) => setSel((p) => { const n = { ...p }; if (v) n[it.id] = "1"; else delete n[it.id]; return n; })} data-testid={`consumable-stock-check-${it.id}`} />
          <span className="min-w-0 flex-1 truncate">{it.name} <span className="text-xs text-muted-foreground">· voľné {Number(it.free_qty ?? it.qty).toLocaleString("sk-SK")} {it.unit}</span></span>
          {sel[it.id] != null && <Input type="number" min="0" max={Number(it.free_qty ?? it.qty)} step={it.divisible ? "0.1" : "1"} className="h-8 w-20" value={sel[it.id]} onChange={(e) => { const mx = Number(it.free_qty ?? it.qty); const v = e.target.value; setSel((p) => ({ ...p, [it.id]: v !== "" && Number(v) > mx ? String(mx) : v })); }} data-testid={`consumable-stock-qty-${it.id}`} />}
        </div>
      ))}
    </div>
  );
}

export function ConsumableSuggest({ open, base, settings, vatMode, onAdd, onSkip, onBack }) {
  const cfg = consumableSettings(settings);
  const amount = suggestAmount(base, cfg);
  const vr = vatMode === "RC" ? 0 : 23;
  const [stock, setStock] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sel, setSel] = useState({});
  useEffect(() => {
    if (!open || cfg.mode !== "stock") return;
    setSel({}); setLoading(true);
    api.get("/warehouse").then((r) => setStock((r.data || []).filter((it) => it.consumable && Number(it.free_qty ?? it.qty) > 0))).catch(() => setStock([])).finally(() => setLoading(false));
  }, [open, cfg.mode]);
  const useStock = cfg.mode === "stock" && stock.length > 0;
  const picked = stock.filter((it) => sel[it.id] != null && Number(sel[it.id]) > 0);
  const add = () => {
    if (!useStock) { onAdd([{ description: "Spotrebný materiál", kind: "material", qty: 1, unit: "ks", unit_price: amount, discount: 0, vat_rate: vr }]); return; }
    const w = picked.map((it) => Number(sel[it.id]) * (Number(it.purchase_price) || 0));
    const tw = w.reduce((s, x) => s + x, 0);
    let left = amount;
    onAdd(picked.map((it, i) => {
      const q = Number(sel[it.id]);
      const share = i === picked.length - 1 ? left : r2(amount * (tw ? w[i] / tw : 1 / picked.length));
      left = r2(left - share);
      return { description: it.name, kind: "material", qty: q, unit: it.divisible ? "l" : (it.unit || "ks"), unit_price: Math.round((share / q) * 10000) / 10000, discount: 0, vat_rate: vr, stock_item_id: it.id };
    }));
  };
  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o) { onBack(); } }}>
      <DialogContent className="sm:max-w-md" data-testid="consumable-suggest-dialog">
        <DialogHeader><DialogTitle className="flex items-center gap-2 font-heading uppercase"><PackagePlus className="h-5 w-5 text-primary" /> Spotrebný materiál</DialogTitle></DialogHeader>
        <div className="text-center">
          <div className="font-heading text-4xl font-bold text-primary" data-testid="consumable-amount">{eur(amount)}</div>
          <div className="mt-1 text-xs text-muted-foreground" data-testid="consumable-calc">{cfg.pct} % z dielov {eur(base)} bez DPH{cfg.min != null ? ` · min ${eur(cfg.min)}` : ""}{cfg.max != null ? ` · max ${eur(cfg.max)}` : ""}</div>
        </div>
        {cfg.mode === "stock" && (loading ? <div className="grid place-items-center py-4"><Loader2 className="h-5 w-5 animate-spin text-primary" /></div> : <StockRows stock={stock} sel={sel} setSel={setSel} />)}
        <DialogFooter className="flex-col gap-2 sm:flex-col sm:space-x-0">
          <Button className="w-full" onClick={add} disabled={useStock && !picked.length} data-testid="consumable-add-save">Pridať a uložiť</Button>
          <Button variant="secondary" className="w-full" onClick={onSkip} data-testid="consumable-skip-save">Uložiť bez spotrebného materiálu</Button>
          <Button variant="ghost" className="w-full" onClick={onBack} data-testid="consumable-back">Späť na úpravu</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
